// @file Store to control the media player on the surface.
import type { Cid } from '@@/types'
import type { CommentCid } from '@@/types/surface_comments'
import { defineStore } from 'pinia'
import { computed, readonly, ref } from 'vue'

type PlayingMediaEntry =
  | {
      type: 'post'
      id: Cid
    }
  | {
      type: 'comment'
      cid: CommentCid
    }

const useSurfaceMediaPlayerStore = defineStore('surfaceMediaPlayer', () => {
  /**
   * Either a post CID or a comment ID since both can have media attachments.
   * Prefixed with `post:` if it's a post CID.
   * Prefixed with `comment:` if it's a comment ID.
   */
  const currentlyPlayingMedia = ref<PlayingMediaEntry | null>(null)

  const currentlyPlayingPostCid = computed<Cid | null>(() => {
    return currentlyPlayingMedia.value?.type === 'post' ? currentlyPlayingMedia.value.id : null
  })

  const currentlyPlayingCommentCid = computed<CommentCid | null>(() => {
    return currentlyPlayingMedia.value?.type === 'comment' ? currentlyPlayingMedia.value.cid : null
  })

  const isPostPlayingMedia = (cid: Cid): boolean => {
    return currentlyPlayingMedia.value?.type === 'post' && currentlyPlayingMedia.value.id === cid
  }

  const isCommentPlayingMedia = (cid: CommentCid | undefined): boolean => {
    return currentlyPlayingMedia.value?.type === 'comment' && currentlyPlayingMedia.value.cid === cid
  }

  const setCurrentlyPlayingMedia = (entry: PlayingMediaEntry): void => {
    currentlyPlayingMedia.value = entry
  }

  const unsetCurrentlyPlayingMedia = (): void => {
    currentlyPlayingMedia.value = null
  }

  return {
    currentlyPlayingMedia: readonly(currentlyPlayingMedia),
    currentlyPlayingPostCid,
    currentlyPlayingCommentCid,
    isPostPlayingMedia,
    isCommentPlayingMedia,
    setCurrentlyPlayingMedia,
    unsetCurrentlyPlayingMedia,
  }
})
export { useSurfaceMediaPlayerStore }
